import {
  getReportByPage,
  type ReportRequestOptions,
} from '@services/api/reports/byPage.api'

export type ReportBucketType = 'month' | 'week' | 'day' | 'hour'

export type ReportPeriodPreset =
  | 'this-year'
  | 'this-month'
  | 'this-week'
  | 'today'
  | 'yesterday'
  | 'last-year'
  | 'last-month'
  | 'last-week'
  | 'last-90-days'
  | 'last-60-days'
  | 'last-30-days'
  | 'last-7-days'
  | 'last-9-months'
  | 'last-6-months'
  | 'last-3-months'
  | 'calendar-q1'
  | 'calendar-q2'
  | 'calendar-q3'
  | 'calendar-q4'
  | 'date-range'
  | 'snapshot'

export interface ReportQueryParams {
  clinicId: string
  periodPreset: ReportPeriodPreset
  referenceDate?: string
  providerId?: string
  startDate?: string
  endDate?: string
  snapshotDate?: string
  snapshotHour?: number
}

export interface ReportPeriodBucket {
  label: string
  value: number
}

export interface ReportKpiScalarResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  value: number
}

export interface ReportPeriodSeriesResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: ReportPeriodBucket[]
}

export interface ReportKpiWithSeriesResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  value: number
  buckets: ReportPeriodBucket[]
}

export interface ReportInsuranceVsPatientResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  insurance: { amount: number; percentage: number }
  patient: { amount: number; percentage: number }
}

export interface ReportHighBalancePatientRow {
  patientId: string
  patientName: string
  balance: number
  daysOutstanding: number
}

export interface ReportHighBalancePatientsResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  patients: ReportHighBalancePatientRow[]
}

export interface ReportRunRatePaceResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  runRate: number
  requiredPace: number
  gap: number
  workdaysElapsed: number
  totalWorkdays: number
}

export interface RevenueRealizationAnalyticsReportResult {
  totalProduction: ReportKpiScalarResult
  totalCollections: ReportKpiScalarResult
  netCollection: ReportKpiScalarResult
  grossCollection: ReportKpiScalarResult
  sameDayCollections: ReportKpiScalarResult
  adjustmentRate: ReportKpiScalarResult
  production: ReportKpiWithSeriesResult
  collection: ReportKpiWithSeriesResult
  collectionForProductionOfPeriod: ReportKpiWithSeriesResult
  netCollectionPercentByPeriod: ReportPeriodSeriesResult
  adjustmentRateByPeriod: ReportPeriodSeriesResult
  insuranceVsPatient: ReportInsuranceVsPatientResult
}

export interface GoalAttainmentVarianceReportResult {
  totalProduction: ReportKpiScalarResult
  totalCollections: ReportKpiScalarResult
  productionByPeriod: ReportPeriodSeriesResult
  collectionByPeriod: ReportPeriodSeriesResult
  productionGoalByPeriod: ReportPeriodSeriesResult
  collectionGoalByPeriod: ReportPeriodSeriesResult
  dailyRunRateVsPace: ReportRunRatePaceResult
}

export interface ReceivablesRiskAnalysisReportResult {
  totalAr: ReportKpiScalarResult
  insuranceAr: ReportKpiScalarResult
  patientAr: ReportKpiScalarResult
  arOver90: ReportKpiScalarResult
  averageDaysInAr: ReportKpiScalarResult
  arTrendByPeriod: ReportPeriodSeriesResult
  arAgingBuckets: ReportPeriodSeriesResult
  averageDaysInArByPeriod: ReportPeriodSeriesResult
  highBalancePatients: ReportHighBalancePatientsResult
}

export interface ClaimsPerformanceReportResult {
  totalClaimsSubmitted: ReportKpiScalarResult
  claimsPending: ReportKpiScalarResult
  claimsOverdue: ReportKpiScalarResult
  claimsDenied: ReportKpiScalarResult
  claimsAcceptanceRate: ReportKpiScalarResult
  resubmissionRate: ReportKpiScalarResult
  averageDaysToPaymentByPeriod: ReportPeriodSeriesResult
  claimsStatusOverview: ReportPeriodSeriesResult
  averageDaysToPaymentByCarrier: ReportPeriodSeriesResult
  insuranceArAgingBuckets: ReportPeriodSeriesResult
}

export const getRevenueRealizationAnalyticsReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<RevenueRealizationAnalyticsReportResult> =>
  getReportByPage<RevenueRealizationAnalyticsReportResult>(
    '/finance/revenue-realization-analytics',
    params,
    options,
  )

export const getGoalAttainmentVarianceReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<GoalAttainmentVarianceReportResult> =>
  getReportByPage<GoalAttainmentVarianceReportResult>(
    '/finance/goal-attainment-variance',
    params,
    options,
  )

export const getReceivablesRiskAnalysisReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<ReceivablesRiskAnalysisReportResult> =>
  getReportByPage<ReceivablesRiskAnalysisReportResult>(
    '/finance/receivables-risk-analysis',
    params,
    options,
  )

export const getClaimsPerformanceReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<ClaimsPerformanceReportResult> =>
  getReportByPage<ClaimsPerformanceReportResult>(
    '/finance/claims-performance',
    params,
    options,
  )
